<script setup lang="ts">
  import type { RouteLocationRaw } from 'vue-router';
  import { DocumentViewInfo as DocumentInfo } from '@/features/Document';
  import { ObservationInfo, TrainingInfo } from '@/features/verification';
  import type { InfoDocumento } from '@/shared/lib';
  import { ChangelogSection, CollaboratorSelectionSection } from '@/shared/ui';
  import SessionCardField from './SessionCardField.vue';

  interface CollaboratorViewData {
    id: number;
    name: string;
    position: string;
  }

  withDefaults(
    defineProps<{
      changelog: string[];
      documentApprovalInfo: InfoDocumento[];
      documentElaborationInfo: InfoDocumento[];
      documentId: string;
      /**
       * Destination of the view button on the elaboration card. Absent by default, which keeps
       * the button delivering the revision file, and that is what both screens did before.
       */
      viewRoute?: RouteLocationRaw;
      instructorsInfo: InfoDocumento[][];
      observation: string[];
      selectedCollaborators: CollaboratorViewData[];
      trainingRequired: boolean;
      trainingJustification: string | null;
      viewButtonLabel?: string;
      showTrainingSection?: boolean;
    }>(),
    { viewButtonLabel: 'Ver documento', showTrainingSection: true }
  );
</script>

<template>
  <div class="verification-details-view divide-y divide-gray-02">
    <div class="p-6">
      <DocumentInfo
        :info="documentElaborationInfo"
        :document-id="documentId"
        :view-route="viewRoute"
        :view-button-label="viewButtonLabel"
      />
    </div>

    <div class="p-6">
      <DocumentInfo
        :info="documentApprovalInfo"
        :document-id="documentId"
        hide-button
      />
    </div>

    <ChangelogSection :changelog="changelog" />

    <TrainingInfo
      v-if="showTrainingSection"
      :training-required="trainingRequired ? 'sim' : 'não'"
      :justification="trainingJustification"
    />

    <div
      v-if="showTrainingSection && trainingRequired"
      class="p-6 flex flex-col gap-6"
    >
      <CollaboratorSelectionSection
        v-if="selectedCollaborators.length"
        title="Colaboradores selecionados para treinamento:"
        :collaborators="selectedCollaborators"
      />

      <div
        v-for="(instructor, index) in instructorsInfo"
        :key="index"
        class="grid gap-6 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_189px]"
      >
        <SessionCardField
          label="Ministrante:"
          :value="instructor.find((item) => item.label === 'Ministrante:')?.value ?? 'Não informado'"
        />
        <SessionCardField
          label="Cargo:"
          :value="instructor.find((item) => item.label === 'Cargo:')?.value ?? 'Cargo não encontrado'"
        />
        <SessionCardField
          label="Data do treinamento:"
          :value="instructor.find((item) => item.label === 'Data do treinamento:')?.value ?? '-'"
        />
      </div>

      <ObservationInfo :observation="observation" />
    </div>
  </div>
</template>
